
import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import OktaSignIn from '@okta/okta-signin-widget';

import { IDToken } from '@okta/okta-auth-js';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { CapacitorOktaStorage } from '../utils/storage/custom-storage';



@Injectable({
  providedIn: 'root',
})
export class OktaService {
  private widget!: OktaSignIn;   // web only
  private oktaAuth: OktaAuth;                 // used by both

  isAuth: boolean = false;
  isNative: boolean = Capacitor.isNativePlatform();
  private authState = new BehaviorSubject<boolean>(false);
  authState$ = this.authState.asObservable();
  private refreshTimer: any;
  calledOnce: boolean = false;
  constructor(private http: HttpClient, private router: Router, private modalController: ModalController) {
    console.log('Okta service called, isNative:', this.isNative);
    const storage = this.isNative ? new CapacitorOktaStorage() : window.localStorage;
    // OktaAuth instance used by both platforms
    this.oktaAuth = new OktaAuth({
      issuer: environment.okta.issuer,
      clientId: environment.okta.clientId,
      redirectUri: environment.okta.redirectUri,
      scopes: environment.okta.scopes,
      pkce: true,
      tokenManager: {
        autoRenew: this.isNative ? false : true,
        expireEarlySeconds: this.isNative ? 10 : 60, // 3 mins
        storage: storage,
      },

      services: {
        autoRenew: false
      }
    });

    // Widget only for web
    if (!this.isNative) {
      this.widget = new OktaSignIn({
        authClient: this.oktaAuth,
        baseUrl: environment.okta.issuer.replace('/oauth2/default', ''),
        clientId: environment.okta.clientId,
        redirectUri: environment.okta.redirectUri,
        logo: 'assets/imgs/logo_black.svg',
        i18n: {
          en: { 'primaryauth.title': 'Sign in to Caluwin' },
        },
        authParams: {
          pkce: true,
          issuer: environment.okta.issuer,
          scopes: environment.okta.scopes,
        },
        features: {
          multiOptionalFactorEnroll: true,
        },
        // registration: {
        //   preSubmit: (postData, onSuccess, onFailure) => {
        //     const locale = navigator.language || 'en';
        //     if (postData['userProfile']) {
        //       (postData['userProfile'] as any)['locale'] = locale;
        //     }
        //     onSuccess(postData);
        //   }
        // }
      });
    }

    // Handle redirect callback on native (app reopened via custom scheme)
    if (this.isNative) {
      App.addListener('appUrlOpen', async ({ url }) => {
        if (url.includes('/callback')) {
          await this.handleNativeCallback(url);
        }
      });
    }

    this.isUserAuthenticated().then();
    this.oktaAuth.tokenManager.on('renewed', (key, newToken, oldToken) => {
      console.log('Token renewed:', key);
    });

    this.oktaAuth.tokenManager.on('expired', (key) => {
      console.log('Token expired:', key);
    });

    this.oktaAuth.tokenManager.on('error', (err) => {
      console.error('Token error:', err);
    });
    this.oktaAuth.tokenManager.on('added', (key, token) => {
      if (this.isNative) {
        Preferences.set({
          key: `okta_${key}`,
          value: JSON.stringify(token)
        });
      }

    });
    this.oktaAuth.start();
  }

  private generateCodeVerifier(): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const bytes = new Uint8Array(43);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((value) => charset.charAt(value % charset.length))
      .join('');
  }
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }


  showSignInToGetTokens(container: string) {
    if (this.isNative) {
      console.error("Widget not available on native platform");
      return;
    }
    this.widget.showSignInToGetTokens({
      el: container
    })
      .then(async (tokens: any) => {
        console.log('Login successful');
        this.oktaAuth.tokenManager.setTokens(tokens);
        const storedTokens = await this.oktaAuth.tokenManager.getTokens();
        console.log('TOKENS (WEB):', storedTokens);
        if (tokens.accessToken) {
          console.log('Access token received');
        }
        // redirect manually if needed
        window.location.reload();
      })
      .catch((err: any) => {
        console.error('Fatal error:', err);
      });
  }

  renderWidget(elementId: string) {
    if (this.isNative || !this.widget) return;
    this.widget.renderEl(
      { el: elementId },
      (res: any) => {
        // Success callback
      },
      (err: any) => {
        throw err;
      }
    );
  }

  removeWidget() {
    if (this.isNative || !this.widget) return;
    this.widget.remove();
  }

  // async isUserAuthenticated(): Promise<boolean> {
  //   const isAuthenticated = await this.widget.authClient.isAuthenticated();
  //   console.log('Is authenticated:', isAuthenticated);
  //   this.isAuth = isAuthenticated;
  //   return isAuthenticated;
  // }

  async isUserAuthenticated(): Promise<boolean> {
    try {
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      this.isAuth = isAuthenticated;
      return isAuthenticated;
    } catch (e) {
      console.error('Auth check failed', e);
      return false;
    }
  }

  async logout() {
    const idToken: any = await this.oktaAuth.tokenManager.get('idToken'); // returns IDToken | undefined
    // await this.oktaAuth.tokenManager.clear();
    // localStorage.removeItem('okta_state');
    // localStorage.removeItem('okta_code_verifier');
    // await Preferences.remove({ key: 'okta_state' });
    // await Preferences.remove({ key: 'okta_code_verifier' });
    if (this.isNative) {
      await this.oktaAuth.tokenManager.clear();
      await Preferences.remove({ key: 'okta_state' });
      await Preferences.remove({ key: 'okta_code_verifier' });

      this.isAuth = false;
      await this.oktaAuth.authStateManager.updateAuthState();
      this.authState.next(false);
      this.router.navigate(['/tabs/landing'], { replaceUrl: true });
      return;
    }
    await this.oktaAuth.signOut({
      idToken,
      postLogoutRedirectUri: window.location.origin
    });
    await this.oktaAuth.authStateManager.updateAuthState();
    this.oktaAuth.stop();
    this.authState.next(false);
  }

  logoutLocal() {
    this.oktaAuth.tokenManager.clear();

    if (!this.isNative) {
      window.location.href = '/login';
    }
  }

  async userInfo() {
    const idToken = await this.oktaAuth.tokenManager.get('idToken') as IDToken;
    return idToken?.claims;
  }

  deactivateUser(userId: string, email: string) {
    let url = environment.caluwin_api.base_url + environment.caluwin_api.endpoints.deleteUser;
    url = url.replace("{userId}", userId);
    url += "?userId=" + email;
    return this.http.post(url, {});
  }

  // Native: called when app reopens via com.sg.p01.caluwin.build:/callback
  async handleNativeCallback(url: string) {
    try {
      await Browser.close();
      console.log("Login Success .... waiting for page ")
      const parsedUrl = new URL(url);
      const code = parsedUrl.searchParams.get('code');
      // const codeVerifier = localStorage.getItem('okta_code_verifier');
      const { value: codeVerifier } = await Preferences.get({ key: 'okta_code_verifier' });

      console.log('code_verifier length:', codeVerifier?.length); // must be 43
      console.log('code_verifier:', codeVerifier);
      if (!code || !codeVerifier) {
        console.error('Missing code or verifier');
        return;
      }

      const { tokens } = await this.oktaAuth.token.exchangeCodeForTokens({
        authorizationCode: code,
        codeVerifier,
        clientId: environment.okta.clientId,
        redirectUri: environment.okta.redirectUri
      });

      console.log('Token response:', tokens);

      this.oktaAuth.tokenManager.setTokens(tokens);
      // await Preferences.set({
      //   key: 'okta-token-storage',
      //   value: JSON.stringify(tokens)
      // });
      this.startTokenExpiryWatcher();
      const storedTokens = await this.oktaAuth.tokenManager.getTokens();
      localStorage.setItem('okta-token-storage', JSON.stringify(storedTokens));
      console.log('TOKENS (NATIVE):', storedTokens);
      this.isAuth = true;
      await this.oktaAuth.authStateManager.updateAuthState();
      console.log('Native login successful');
      await Preferences.remove({ key: 'okta_state' });
      await Preferences.remove({ key: 'okta_code_verifier' });

      const modal = await this.modalController.getTop();
      if (modal) {
        await modal.dismiss(null, 'confirm');
      }
      this.authState.next(true);
      // Navigate to home
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/tabs/landing'], { replaceUrl: true });
      });

    } catch (err) {
      console.error('Native callback error:', err);
    }
  }

  async login(div: string) {
    if (!this.isNative) {
      // WEB LOGIN
      this.showSignInToGetTokens(div);
      return;
    }
    const modal = await this.modalController.getTop();
    if (modal) {
      await modal.dismiss(null, 'confirm');
    }

    // NATIVE LOGIN
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    // const { codeVerifier, codeChallenge } =
    //   await this.oktaAuth.token.prepareTokenParams();

    if (!codeVerifier || !codeChallenge) {
      throw new Error('PKCE generation failed');
    }
    const state = Math.random().toString(36).substring(2);


    console.log("inside login", codeVerifier);
    console.log("inside login codeChallenge", codeChallenge);

    if (this.isNative) {
      await Preferences.set({ key: 'okta_state', value: state });
      await Preferences.set({ key: 'okta_code_verifier', value: codeVerifier });
    } else {
      localStorage.setItem('okta_state', state);
    }
    // localStorage.setItem('okta_code_verifier', codeVerifier);
    console.log('Stored code_verifier length:', codeVerifier.length);

    const authorizeUrl =
      `${environment.okta.issuer}/v1/authorize` +
      `?client_id=${environment.okta.clientId}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(environment.okta.scopes.join(' '))}` +
      `&redirect_uri=${encodeURIComponent(environment.okta.redirectUri)}` +
      `&state=${state}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&prompt=consent`;

    await Browser.open({ url: authorizeUrl });
  }

  private parseJwtClaims(token: string): any {
    try {
      const payload = token.split('.')[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json);
    } catch (e) {
      console.error('Failed to parse token claims', e);
      return {};
    }
  }

  public startTokenExpiryWatcher() {
    if (this.calledOnce) {
      return;
    }
    this.calledOnce = true;
    console.log('Starting token expiry watcher...');
    this.refreshTimer = setInterval(async () => {
      const accessToken = await this.oktaAuth.tokenManager.get('accessToken');

      if (!accessToken) return;

      const expiresAtMs = accessToken.expiresAt * 1000;
      const now = Date.now();

      const secondsLeft = Math.floor((expiresAtMs - now) / 1000);

      console.log('Token expires in:', secondsLeft, 'seconds');

      // refresh when <= 1 minutes
      if (secondsLeft <= 60) {
        console.log('Refreshing access token...');

        try {
          await this.refreshAccessToken();
        } catch (err) {
          console.error('Token refresh failed:', err);

        }
      }
    }, 10000); // check every 10 seconds
  }
  stopTokenWatcher() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  async refreshAccessToken() {
    const refreshToken: any = await this.oktaAuth.tokenManager.get('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: environment.okta.clientId,
      refresh_token: refreshToken.refreshToken,
      scope: environment.okta.scopes.join(' ')
    });

    const response = await fetch(`${environment.okta.issuer}/v1/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body
    });

    const tokens = await response.json();
    if (!response.ok) {
      throw new Error(`Token refresh failed: ${tokens?.error || response.status}`);
    }

    console.log("found new token: ", tokens)
    // await this.oktaAuth.tokenManager.clear();
    const accessClaims = tokens.access_token ? this.parseJwtClaims(tokens.access_token) : {};
    const idClaims = tokens.id_token ? this.parseJwtClaims(tokens.id_token) : {};

    await this.oktaAuth.tokenManager.setTokens({
      accessToken: {
        accessToken: tokens.access_token,
        expiresAt: Date.now() / 1000 + tokens.expires_in,
        scopes: tokens.scope?.split(' '),
        claims: accessClaims,
        tokenType: tokens.token_type || '',
        userinfoUrl: tokens.userinfo_url || '',
        authorizeUrl: tokens.authorize_url || ''
      },

      idToken: {
        idToken: tokens.id_token,
        claims: idClaims,
        issuer: '',
        clientId: '',
        expiresAt: Date.now() / 1000 + tokens.expires_in,
        authorizeUrl: '',
        scopes: []
      },

      refreshToken: {
        refreshToken: tokens.refresh_token,
        tokenUrl: '',
        issuer: '',
        expiresAt: Date.now() / 1000 + tokens.expires_in,
        authorizeUrl: '',
        scopes: []
      }
    });
    // await Preferences.set({
    //   key: 'okta-token-storage',
    //   value: JSON.stringify(tokens)
    // });
    const storedTokens = await this.oktaAuth.tokenManager.getTokens();
    console.log('stored tokens after refresh:', storedTokens);
    console.log('stored idToken claims:', storedTokens.idToken?.claims);
    return storedTokens;
  }

  async getAccessTokenOrNull(): Promise<string | null> {
    try {
      const token: any = await this.oktaAuth.tokenManager.get('accessToken');
      return token?.accessToken ?? null;
    } catch (error) {
      console.error('Failed to read access token', error);
      return null;
    }
  }

  async tryRefreshAccessToken(): Promise<string | null> {
    try {
      await this.refreshAccessToken();
      return this.getAccessTokenOrNull();
    } catch (error) {
      console.error('Silent token refresh failed', error);
      return null;
    }
  }

  async triggerReauthentication(): Promise<void> {
    if (this.isNative) {
      await this.login('');
      return;
    }

    await this.router.navigateByUrl('/login');
  }

}
